import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from '../../../common/hooks/useDebounce';
import {
  isGoogleMapsLoaded,
  waitForGoogleMaps,
} from '../../../common/utils/googlePlaces';
import {
  MAX_SEARCH_QUERY_LENGTH,
  MAX_SEARCH_RESULTS,
  MIN_SEARCH_QUERY_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
} from '../constants';
import { Doctor } from '../types';

/**
 * Hook for searching doctors using Google Places API (frontend-only)
 * @param applicationId - Application ID (kept for future use)
 * @param taskId - Task ID (kept for future use)
 * @param query - Search query string
 * @param enabled - Whether search should be enabled
 * @returns Object with doctors array and loading state
 */
const useDoctorsSearch = (
  _applicationId: number,
  _taskId: number,
  query: string,
  enabled = true,
) => {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_DELAY_MS);
  const currentRequestRef = useRef<number>(0);

  const convertPredictionToDoctor = useCallback(
    (
      prediction: google.maps.places.QueryAutocompletePrediction,
      index: number,
    ): Doctor => {
      const structuredFormatting = (prediction as any).structured_formatting as
        | { main_text?: string; secondary_text?: string }
        | undefined;
      const name =
        structuredFormatting?.main_text || prediction.description || '';
      const description =
        structuredFormatting?.secondary_text || prediction.description || '';

      const parts = description.split(',').map((part: string) => part.trim());
      let city = '';
      let state = '';
      let zipCode = '';

      if (parts.length >= 2) {
        const lastPart = parts[parts.length - 1];
        const stateZipMatch = lastPart.match(
          /^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/,
        );
        if (stateZipMatch) {
          state = stateZipMatch[1];
          zipCode = stateZipMatch[2];
          if (parts.length >= 3) {
            city = parts[parts.length - 2];
          }
        } else {
          city = parts.length >= 3 ? parts[parts.length - 2] : '';
          const stateZip = parts[parts.length - 1];
          const zipMatch = stateZip.match(/\d{5}(?:-\d{4})?/);
          if (zipMatch) {
            zipCode = zipMatch[0];
            state = stateZip.replace(zipMatch[0], '').trim();
          }
        }
      }

      const address = parts.length > 2 ? parts.slice(0, -2).join(', ') : '';
      // Use place_id as primary identifier (always unique from Google)
      // Include index in fallback to ensure uniqueness even if place_id is missing
      const uniqueId =
        prediction.place_id ||
        `google_places_${index}_${Date.now()}_${prediction.description}`;
      return {
        id: uniqueId,
        placeId: prediction.place_id,
        source: 'google_places',
        name: name.trim(),
        information: description,
        providerNPI: '',
        city,
        state,
        zipCode,
        phoneNumber: '',
        address,
      };
    },
    [],
  );

  useEffect(() => {
    if (!enabled) {
      setDoctors([]);
      setIsLoading(false);
      return;
    }

    const searchDoctors = async () => {
      const trimmedQuery = debouncedQuery.trim();

      if (
        !trimmedQuery ||
        trimmedQuery.length < MIN_SEARCH_QUERY_LENGTH ||
        trimmedQuery.length > MAX_SEARCH_QUERY_LENGTH
      ) {
        setDoctors([]);
        setIsLoading(false);
        return;
      }

      const requestId = ++currentRequestRef.current;

      // Wait for Google Maps API to load if needed
      if (!isGoogleMapsLoaded()) {
        setIsLoading(true);
        try {
          await waitForGoogleMaps();
        } catch (err) {
          console.error('[DoctorsSearch] Google Maps API failed to load:', err);
          setDoctors([]);
          setIsLoading(false);
          return;
        }
      }

      setIsLoading(true);

      try {
        // Create AutocompleteService instance
        // Why: This is the Google Places service for searching places
        const autoCompleteService =
          new window.google.maps.places.AutocompleteService();

        // Make search request
        autoCompleteService.getPlacePredictions(
          {
            input: trimmedQuery,
            componentRestrictions: { country: 'us' },
            types: ['hospital', 'doctor'],
          },
          (predictions, status) => {
            // Check if this response is from the current request
            // Ignore results from outdated requests to prevent race conditions
            if (requestId !== currentRequestRef.current) {
              return;
            }

            setIsLoading(false);

            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              const limitedPredictions = predictions.slice(
                0,
                MAX_SEARCH_RESULTS,
              );
              const doctorsList = limitedPredictions.map((prediction, index) =>
                convertPredictionToDoctor(prediction, index),
              );
              setDoctors(doctorsList);
            } else {
              setDoctors([]);
            }
          },
        );
      } catch (err) {
        console.error('[DoctorsSearch] Error searching doctors:', err);
        setDoctors([]);
        setIsLoading(false);
      }
    };

    searchDoctors();

    return () => {
      currentRequestRef.current++;
    };
  }, [debouncedQuery, enabled, convertPredictionToDoctor]);

  return {
    doctors,
    isLoading: enabled ? isLoading : false,
  };
};

export default useDoctorsSearch;
